import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  TextField,
  InputAdornment,
  Container,
  CircularProgress,
  Stack,
  Tooltip,
  Snackbar,
  Avatar,
  TablePagination,
  Alert,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Assignment as AssignmentIcon,
  BugReport as BugIcon,
  Build as BuildIcon,
  Lightbulb as FeatureIcon,
  PriorityHigh as PriorityHighIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import api from '../../../services/api';
import TicketDetailsModal from './TicketDetailsModal';
import TicketCreateModal from './TicketCreateModal';
import TicketEditModal from './TicketEditModal';
import TicketAssignModal from './TicketAssignModal';

const TicketManagement = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // all | OPEN | IN_PROGRESS | CLOSED
  const [searchBy, setSearchBy] = useState('ticketNumber'); // ticketNumber | title | assignedTo
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState({ term: '', by: 'ticketNumber' });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    totalElements: 0,
    sortField: 'id',
    sortDirection: 'asc'
  });

  // Fetch tickets from API
  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        size: pagination.size,
        sortField: pagination.sortField,
        direction: pagination.sortDirection
      };

      // Status filter
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      // Search
      if (appliedSearch.term) {
        params.search = appliedSearch.term;
        params.searchField = appliedSearch.by;
      }

      const response = await api.get('/v1/api/tickets', { params });

      setTickets(response.data.content);
      setPagination(prev => ({
        ...prev,
        totalElements: response.data.page.totalElements,
        totalPages: response.data.page.totalPages
      }));
    } catch (error) {
      showSnackbar('Failed to load tickets', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [pagination.page, pagination.size, pagination.sortField, pagination.sortDirection, statusFilter, appliedSearch]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleMenuOpen = (event, ticket) => {
    setAnchorEl(event.currentTarget);
    setSelectedTicket(ticket);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const updateTicketStatus = (id, status) => {
    setTickets(tickets.map(ticket => ticket.id === id ? { ...ticket, status } : ticket));
  };

  const handleCreateTicket = async (ticketData) => {
    try {
      const response = await api.post('/v1/api/tickets', ticketData);
      setTickets([response.data, ...tickets]);
      showSnackbar('Ticket created successfully', 'success');
      setCreateOpen(false);
      fetchTickets(); // Refresh the list
    } catch (error) {
      showSnackbar('Failed to create ticket', 'error');
    }
  };

  const handleUpdateTicket = async (updatedData) => {
    if (!selectedTicket) return;

    try {
      const response = await api.put(`/v1/api/tickets/${selectedTicket.id}`, updatedData);
      setTickets(tickets.map(ticket =>
        ticket.id === selectedTicket.id ? response.data : ticket
      ));
      showSnackbar('Ticket updated successfully', 'success');
      setEditOpen(false);
      handleMenuClose();
    } catch (error) {
      showSnackbar('Failed to update ticket', 'error');
    }
  };

  const handleAssignTicket = async (employeeId) => {
    if (!selectedTicket) return;

    try {
      const response = await api.put(`/v1/api/tickets/${selectedTicket.id}/assign`, { employeeId });
      setTickets(tickets.map(ticket =>
        ticket.id === selectedTicket.id ? response.data : ticket
      ));
      showSnackbar('Ticket assigned successfully', 'success');
      setAssignOpen(false);
      handleMenuClose();
    } catch (error) {
      showSnackbar('Failed to assign ticket', 'error');
    }
  };

  const handleDeleteTicket = async () => {
    if (!selectedTicket) return;

    try {
      await api.delete(`/v1/api/tickets/${selectedTicket.id}`);
      setTickets(tickets.filter(ticket => ticket.id !== selectedTicket.id));
      showSnackbar('Ticket deleted successfully', 'success');
      handleMenuClose();
    } catch (error) {
      showSnackbar('Failed to delete ticket', 'error');
    }
  };

  const handleSubmitSearch = () => {
    setPagination(prev => ({ ...prev, page: 0 }));
    setAppliedSearch({ term: searchTerm.trim(), by: searchBy });
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setAppliedSearch({ term: '', by: 'ticketNumber' });
    setSearchBy('ticketNumber');
    setStatusFilter('all');
    setPagination(prev => ({ ...prev, page: 0 }));
    fetchTickets();
    showSnackbar('Tickets refreshed', 'info');
  };

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleSizeChange = (event) => {
    setPagination(prev => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0
    }));
  };

  const calculateSlaStatus = (dueDate) => {
    if (!dueDate) return { status: 'N/A', color: 'default' };

    const now = new Date();
    const due = new Date(dueDate);
    const timeDiff = due - now;
    const daysDiff = timeDiff / (1000 * 3600 * 24);

    if (daysDiff < 0) {
      return { status: 'Breached', color: 'error' };
    } else if (daysDiff < 1) {
      return { status: 'Due Soon', color: 'warning' };
    } else {
      return { status: 'On Track', color: 'success' };
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'HIGH':
      case 'CRITICAL':
        return <PriorityHighIcon color="error" fontSize="small" />;
      default:
        return <PriorityHighIcon fontSize="small" />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'BUG':
        return <BugIcon color="error" fontSize="small" />;
      case 'Feature Request':
        return <FeatureIcon color="primary" fontSize="small" />;
      case 'Technical Issue':
        return <BuildIcon color="action" fontSize="small" />;
      default:
        return <AssignmentIcon fontSize="small" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Incident Ticket Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ px: 3 }}
          >
            Create Ticket
          </Button>
        </Stack>

        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            {/* Unified search control */}
            <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
              <FormControl
                size="small"
                sx={{
                  minWidth: 180,
                  mr: 0,
                  '& .MuiOutlinedInput-root': {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0
                  }
                }}
              >
                <InputLabel id="search-by-label">Search by</InputLabel>
                <Select
                  labelId="search-by-label"
                  id="search-by"
                  value={searchBy}
                  label="Search by"
                  onChange={(e) => setSearchBy(e.target.value)}
                >
                  <MenuItem value="ticketNumber">Ticket #</MenuItem>
                  <MenuItem value="title">Title</MenuItem>
                  <MenuItem value="assignedTo">Assignee</MenuItem>
                </Select>
              </FormControl>

              <TextField
                placeholder={`Search by ${searchBy === 'ticketNumber' ? 'Ticket #' : searchBy === 'assignedTo' ? 'Assignee' : 'Title'}...`}
                variant="outlined"
                size="small"
                sx={{
                  maxWidth: 600,
                  ml: '-1px',
                  mr: 0,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0
                  }
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmitSearch();
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />

              <Button
                variant="contained"
                size="small"
                onClick={handleSubmitSearch}
                disabled={!searchTerm.trim()}
                sx={{
                  height: '40px',
                  ml: '-1px',
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  whiteSpace: 'nowrap'
                }}
              >
                Search
              </Button>
            </Box>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="OPEN">Open</MenuItem>
                <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                <MenuItem value="CLOSED">Closed</MenuItem>
              </Select>
            </FormControl>

            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Filters">
              <IconButton>
                <FilterListIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: '65vh' }}>
                <Table size="small" stickyHeader sx={{
                  '& .MuiTableCell-root': { py: 0.5, fontSize: '0.82rem' },
                  '& .MuiTableCell-head': { py: 0.5, fontSize: '0.8rem', fontWeight: 600, letterSpacing: 0.1 },
                  '& tbody tr:hover': { bgcolor: 'action.hover' },
                  '& tbody tr:nth-of-type(odd)': { bgcolor: 'background.default' }
                }}>
                  <TableHead sx={{ bgcolor: 'background.default' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Ticket #</TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Priority</TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Assigned To</TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 0.5 }}>SLA</TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Due Date</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, py: 0.5 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tickets.length > 0 ? (
                      tickets?.map((ticket) => {
                        const sla = calculateSlaStatus(ticket.due);
                        return (
                          <TableRow key={ticket.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {ticket.ticketNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 360 }}>
                                {ticket.title}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                {getTypeIcon(ticket.type)}
                                <Typography variant="body2">{ticket.type}</Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                {getPriorityIcon(ticket.priority)}
                                <Typography variant="body2">{ticket.priority}</Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              {ticket.assignedTo ? (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar
                                    src={ticket.assignedTo.avatar}
                                    sx={{ width: 24, height: 24 }}
                                  >
                                    {ticket.assignedTo?.charAt(0)}
                                  </Avatar>
                                  <Typography variant="body2">{ticket.assignedTo}</Typography>
                                </Stack>
                              ) : (
                                <Chip
                                  label="Unassigned"
                                  size="small"
                                  variant="outlined"
                                  color="default"
                                  icon={<PersonIcon fontSize="small" />}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={ticket.status}
                                color={
                                  ticket.status === 'OPEN' ? 'info' :
                                    ticket.status === 'IN_PROGRESS' ? 'success' :
                                      ticket.status === 'CLOSED' ? 'primary' : 'default'
                                }
                                size="small"
                                sx={{ textTransform: 'capitalize' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={sla.status}
                                color={sla.color}
                                size="small"
                                icon={<ScheduleIcon fontSize="small" />}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {formatDate(ticket.due)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="View Details">
                                <IconButton size="small" sx={{ p: 0.5 }} onClick={() => {
                                  setSelectedTicket(ticket);
                                  setDetailsOpen(true);
                                }}>
                                  <AssignmentIcon color="info" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Actions">
                                <IconButton size="small" sx={{ p: 0.5 }} onClick={(e) => handleMenuOpen(e, ticket)}>
                                  <MoreVertIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Typography variant="body1" color="text.secondary">
                            No tickets found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={pagination.totalElements}
                rowsPerPage={pagination.size}
                page={pagination.page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleSizeChange}
                sx={{ borderTop: '1px solid rgba(224, 224, 224, 1)' }}
              />
            </>
          )}
        </Paper>
      </Box>

      {/* Modals */}
      <TicketDetailsModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        ticket={selectedTicket}
        updateTicketStatus={updateTicketStatus}
      />

      <TicketCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreateTicket}
      />

      <TicketEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        ticket={selectedTicket}
        onSubmit={handleUpdateTicket}
      />

      <TicketAssignModal
        open={assignOpen}
        onClose={() => {
          setAssignOpen(false);
          setSelectedTicket(null);
        }}
        onAssign={handleAssignTicket}
      />

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          setEditOpen(true);
          handleMenuClose();
        }}>
          <AssignmentIcon sx={{ mr: 1 }} />
          Edit Ticket
        </MenuItem>
        <MenuItem onClick={() => {
          setAssignOpen(true);
          handleMenuClose();
        }}>
          <PersonIcon sx={{ mr: 1 }} />
          Assign Ticket
        </MenuItem>
        <MenuItem onClick={handleDeleteTicket}>
          <CloseIcon sx={{ mr: 1 }} />
          Delete Ticket
        </MenuItem>
      </Menu>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TicketManagement;